import React, { useId } from 'react';
import ReactModal from 'react-modal';
import { Button } from '@components/atoms/Button';
import { AlertTriangle } from 'lucide-react';
import ThumbUpAltIcon from '@mui/icons-material/ThumbUpAlt';
import ClearIcon from '@mui/icons-material/Clear';
import { ConfirmationModalProps } from './ConfirmationModal.types';
import { useModalManager } from '@utils/modalManager';
import { Z_INDEX } from '@constants/zIndex';
import { useTranslation } from '@hooks/useTranslation';
import '@utils/modalSetup';

export const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
  isOpen,
  children,
  onClose,
  onConfirm,
  title,
  description,
  confirmText,
  cancelText,
  secondaryText,
  onSecondary,
  variant = 'danger',
  isLoading = false,
  closeOnConfirm = true,
  size = 'small',
  showCloseButton = true,
  closeOnEscape = true,
  closeOnOverlayClick = true,
  preventScroll = true,
  className = '',
  overlayClassName = '',
  contentClassName = '',
}) => {
  const modalId = useId();
  const modalManager = useModalManager();
  // Sprint 28, story-142. Decision 2 (docs/architecture.md "## Sprint 28") — a shared component
  // may own `common.*` defaults for its own generic copy; the title/message stay caller-supplied.
  const tCommon = useTranslation('common');
  const resolvedTitle = title ?? tCommon.confirmActionTitle;
  const resolvedDescription = description ?? tCommon.confirmActionDescription;
  const resolvedConfirmText = confirmText ?? tCommon.confirm;
  const resolvedCancelText = cancelText ?? tCommon.cancel;

  React.useEffect(() => {
    if (isOpen) {
      modalManager.openModal(modalId, 'dialog', preventScroll);
    } else {
      modalManager.closeModal(modalId);
    }

    return () => {
      modalManager.closeModal(modalId);
    };
  }, [isOpen, modalId, modalManager, preventScroll]);

  // Get size classes based on size prop
  const getSizeClasses = () => {
    switch (size) {
      case 'small':
        return 'w-96 max-w-[90vw]';
      case 'medium':
        return 'w-[500px] max-w-[90vw]';
      case 'large':
        return 'w-[600px] max-w-[90vw]';
      default:
        return 'w-96 max-w-[90vw]';
    }
  };

  // Get overlay classes
  const getOverlayClasses = () => {
    const baseClasses =
      'fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-5';
    return `${baseClasses} ${overlayClassName}`;
  };

  // Get content classes
  const getContentClasses = () => {
    const baseClasses =
      'relative bg-white dark:bg-gray-800 rounded-lg shadow-xl outline-none max-h-[90vh] overflow-visible transition-all duration-300 ease-in-out';
    const sizeClasses = getSizeClasses();
    return `${baseClasses} ${sizeClasses} ${contentClassName}`;
  };

  // Get variant styles
  const getVariantStyles = () => {
    switch (variant) {
      case 'danger':
        return {
          iconColor: 'text-red-500',
          iconBg: 'bg-red-100 dark:bg-red-900/20',
          confirmVariant: 'primary' as const,
          confirmClassName: 'bg-red-600 hover:bg-red-700 active:bg-red-800',
        };
      case 'warning':
        return {
          iconColor: 'text-yellow-500',
          iconBg: 'bg-yellow-100 dark:bg-yellow-900/20',
          confirmVariant: 'primary' as const,
          confirmClassName:
            'bg-yellow-600 hover:bg-yellow-700 active:bg-yellow-800',
        };
      case 'info':
        return {
          iconColor: 'text-blue-500',
          iconBg: 'bg-blue-100 dark:bg-blue-900/20',
          confirmVariant: 'primary' as const,
          confirmClassName: 'bg-blue-600 hover:bg-blue-700 active:bg-blue-800',
        };
      case 'success':
        return {
          iconColor: 'text-green-500',
          iconBg: 'bg-green-100 dark:bg-green-900/20',
          confirmVariant: 'primary' as const,
          confirmClassName:
            'bg-green-600 hover:bg-green-700 active:bg-green-800',
        };
      default:
        return {
          iconColor: 'text-gray-500',
          iconBg: 'bg-gray-100 dark:bg-gray-700',
          confirmVariant: 'primary' as const,
          confirmClassName:
            'bg-indigo-600 hover:bg-indigo-700 active:bg-indigo-800',
        };
    }
  };

  const getIcon = () => {
    switch (variant) {
      case 'success':
        return (
          <ThumbUpAltIcon className={`w-5 h-5 ${variantStyles.iconColor}`} />
        );
      default:
        return (
          <AlertTriangle className={`w-5 h-5 ${variantStyles.iconColor}`} />
        );
    }
  };

  const variantStyles = getVariantStyles();

  const handleClose = () => {
    if (isLoading) return;
    if (onClose) {
      onClose();
    }
  };

  const handleConfirm = () => {
    if (onConfirm) {
      onConfirm();
    }
    if (closeOnConfirm) {
      handleClose();
    }
  };

  return (
    <ReactModal
      isOpen={isOpen}
      ariaHideApp={false}
      onRequestClose={handleClose}
      className={getContentClasses()}
      shouldCloseOnEsc={closeOnEscape}
      overlayClassName={getOverlayClasses()}
      shouldCloseOnOverlayClick={closeOnOverlayClick}
      style={{
        content: { zIndex: Z_INDEX.MODAL_CONTENT },
        overlay: { zIndex: Z_INDEX.MODAL_BACKDROP },
      }}>
      <div
        className={`confirmation-modal-container relative transition-all duration-500 ease-in-out ${className}`}>
        {/* Header with title and close button */}
        <div className="flex items-center justify-between p-6 pb-4">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-full ${variantStyles.iconBg}`}>
              {getIcon()}
            </div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white m-0">
              {resolvedTitle}
            </h2>
          </div>
          {showCloseButton && onClose && (
            <div
              onClick={handleClose}
              className="p-1 text-gray-400 rounded-full transition-colors duration-200 cursor-pointer hover:text-gray-800"
              aria-label={tCommon.close}>
              <ClearIcon className="w-5 h-5" />
            </div>
          )}
        </div>

        {/* Modal content */}
        <div className="px-6 pb-6">
          <p className="text-gray-600 dark:text-gray-300 text-sm leading-relaxed">
            {resolvedDescription}
          </p>
          {children}
        </div>

        {/* Action buttons */}
        <div className="flex items-center justify-end gap-3 px-6 pb-6 border-t border-gray-200 dark:border-gray-600 pt-4">
          <Button variant="secondary" onClick={handleClose} size="md" disabled={isLoading}>
            {resolvedCancelText}
          </Button>
          {secondaryText && onSecondary && (
            <Button
              variant="secondary"
              size="md"
              onClick={() => {
                onSecondary();
                handleClose();
              }}>
              {secondaryText}
            </Button>
          )}
          <Button
            size="md"
            variant={variantStyles.confirmVariant}
            onClick={handleConfirm}
            loading={isLoading}
            className={variantStyles.confirmClassName}>
            {resolvedConfirmText}
          </Button>
        </div>
      </div>
    </ReactModal>
  );
};
